export function campaignSimulationEnabled({ hostname = '', environment = '', enabled = '' } = {}) {
  const stage = environment.toLowerCase().trim();
  if (['production', 'prod'].includes(stage)) return false;
  const local = ['localhost', '127.0.0.1', '::1', '[::1]'].includes(hostname);
  return local || enabled === 'true' && ['homologation', 'homologacao', 'homolog', 'staging'].includes(stage);
}

const students = ['Demo Student A', 'Demo Student B', 'Demo Student C', 'Demo Student D', 'Demo Student E', 'Demo Student F'];
const amounts = [100, 200, 150, 80, 120, 60, 90, 200];
const owners = [0, 0, 1, 2, 3, 4, 5, 5];
const outcomes = [['accepted', 'accepted'], ['accepted', 'failed'], ['failed', 'failed'], ['accepted', 'unknown'], ['blocked', 'blocked'], ['accepted', 'accepted']];

export function buildCampaignSimulation(stage = 'before', now = new Date()) {
  const sentAt = new Date(now.getTime() - 86400000).toISOString();
  const recordedAt = new Date(now.getTime() - 3600000).toISOString();
  const day = recordedAt.slice(0, 10);
  const messages = students.flatMap((name, student) => ['email', 'sms'].map((channel, index) => ({
    id: `demo-message-${student}-${channel}`, student_id: `demo-student-${student}`, student_name: name,
    channel, destination: channel === 'email' ? `student${student + 1}@example.test` : `+1 202 555 010${student}`,
    status: outcomes[student][index],
  })));
  const summary = { invoices: amounts.length, unavailable: 0, outstanding_at_confirmation: amounts.length, outstanding_now: 0, paid_now: 0, partially_paid_now: 0, paid_after_send: 0, invoices_with_payments_after_send: 0, payments_recorded_after_send: 0, zelle_recorded_after_send: 0, balance_at_confirmation: 1000, balance_now: 0, unlinked_deliveries: 0 };
  const messageSummary = { accepted: 0, failed: 0, blocked: 0, unknown: 0, pending: 0, processing: 0 };
  messages.forEach(message => { messageSummary[message.status] += 1; });
  const rows = amounts.map((amount, index) => {
    const student = owners[index];
    const attempts = messages.filter(message => message.student_id === `demo-student-${student}`);
    const accepted = attempts.filter(message => message.status === 'accepted').length;
    let balance = amount;
    if (stage === 'partial') balance = ({ 0: 0, 1: 150, 2: 0, 7: 100 })[index] ?? amount;
    if (stage === 'paid') balance = 0;
    const status = balance === 0 ? 'Paid' : balance < amount ? 'Partial Paid' : 'Pending';
    const received = accepted ? amount - balance : 0;
    const method = index % 2 ? 'Online payment' : 'Zelle';
    summary.balance_now += balance;
    if (balance > 0) summary.outstanding_now += 1;
    if (status === 'Paid') summary.paid_now += 1;
    if (status === 'Partial Paid') summary.partially_paid_now += 1;
    if (status === 'Paid' && received > 0) summary.paid_after_send += 1;
    if (received > 0) summary.invoices_with_payments_after_send += 1;
    summary.payments_recorded_after_send += received;
    if (method === 'Zelle') summary.zelle_recorded_after_send += received;
    return {
      id: `demo-invoice-${index}`, student_id: `demo-student-${student}`, student_name: students[student], registration_number: `DEMO-${student + 1}`,
      invoice_number: `DEMO-${String(index + 1).padStart(3, '0')}`, due_date: sentAt.slice(0, 10),
      status_at_confirmation: 'Pending', balance_at_confirmation: amount, payment_method_at_confirmation: method,
      available: true, status_now: status, balance_now: balance, payment_method_now: method,
      channels: ['email', 'sms'], accepted_messages: accepted,
      failed_messages: attempts.filter(message => message.status === 'failed').length,
      blocked_messages: attempts.filter(message => message.status === 'blocked').length,
      first_accepted_at: accepted ? sentAt : null, paid_after_send: status === 'Paid' && received > 0,
      payments_recorded_after_send: received, payment_link_sent: accepted > 0 && index % 2 === 1,
      payments: received > 0 ? [{ id: `demo-payment-${index}`, amount: received, payment_method: method, settlement_date: day, cash_date: day, recorded_at: recordedAt }] : [],
    };
  });
  return { tracking: { enabled: true, available: true, source: 'simulation', generated_at: now.toISOString(), summary, rows }, messageSummary, messages };
}

export function buildSimulatedSentMessages() {
  const { messages } = buildCampaignSimulation('before');
  return students.map((name, student) => ({
    student_id: `demo-student-${student}`, student_name: name, registration_number: `DEMO-${student + 1}`,
    messages: messages.filter(message => message.student_id === `demo-student-${student}`).map(message => ({
      channel: message.channel, destination: message.destination, status: message.status,
      subject: message.channel === 'sms' ? '' : 'Payment reminder',
      body: `Dear ${name}, this is a reminder that your payment is pending. Pay online: https://example.test/pay/demo-${student + 1}\n\nMILA Miami International Language Academy`,
    })),
  }));
}
